<template>
  <main class="mobile-only" aria-labelledby="mobile-only-title" aria-describedby="mobile-only-text">
    <div class="mobile-only__inner">
      <SyntryLogo size="sm" class="mobile-only__logo" />

      <!-- Product chrome: --syntry-mark tokens, which the tenant brand color
           never repaints (see src/theme/brand-color.ts). -->
      <div class="mobile-only__icon-ring" aria-hidden="true">
        <ion-icon :icon="phonePortraitOutline" />
      </div>

      <p class="syntry-eyebrow mobile-only__eyebrow">Mobile only</p>
      <h1 id="mobile-only-title" class="mobile-only__title">
        Syntry is only usable on mobile platforms
      </h1>
      <p id="mobile-only-text" class="mobile-only__text">
        Open this link on your iPhone, iPad or Android device — or use the installed
        Syntry app — to continue.
      </p>

      <button
        type="button"
        class="mobile-only__btn"
        aria-describedby="mobile-only-note"
        @click="learnMore"
      >
        <ion-icon :icon="openOutline" aria-hidden="true" />
        Learn more
      </button>
      <p id="mobile-only-note" class="mobile-only__note">Opens {{ hostname }} in a new tab.</p>

      <p v-if="showDiagnostic" class="mobile-only__diag">
        Detected: desktop · {{ reason }}. Append <code>?mobile_gate=off</code> to the URL to bypass.
      </p>
    </div>
  </main>
</template>

<script setup lang="ts">
/**
 * The mobile-only wall — mounted as its own Vue root by
 * `src/pwa/mobile-gate.ts` when the app is opened in a desktop browser.
 *
 * Stateless by design: no router, no session, no store. Theme (`body.dark`)
 * is applied by the host before mount, so this page only needs tokens.
 */
import { computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { openOutline, phonePortraitOutline } from 'ionicons/icons';
import SyntryLogo from '@/components/SyntryLogo.vue';
import { SYNTRY_WEBSITE_URL } from '@/utils/app-info';
import { IS_DEV } from '@/utils/env';

const props = defineProps<{
  /** Which detection step produced the "desktop" verdict (dev-only hint). */
  reason?: string;
}>();

const hostname = new URL(SYNTRY_WEBSITE_URL).hostname;
const showDiagnostic = computed(() => IS_DEV && !!props.reason);

/** Repo convention for external links (see src/utils/gov-portals.ts). */
function learnMore(): void {
  window.open(SYNTRY_WEBSITE_URL, '_blank', 'noopener');
}
</script>

<style scoped lang="scss">
.mobile-only {
  position: fixed;
  inset: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--syntry-bg);
  color: var(--syntry-text);
  font-family: var(--syntry-font);
  /* Zero on desktops; still correct if a phone is ever forced onto the wall. */
  padding:
    calc(var(--sp-8) + var(--syntry-safe-top, env(safe-area-inset-top, 0px)))
    var(--sp-6)
    calc(var(--sp-8) + var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)));

  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    max-width: 460px;
  }

  &__logo {
    margin-bottom: var(--sp-4);
  }

  &__icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    margin-bottom: var(--sp-2);
    background: var(--syntry-mark-soft);
    color: var(--syntry-mark);
  }

  &__eyebrow {
    margin: 0;
  }

  &__title {
    font-size: var(--syntry-fs-h2);
    font-weight: 800;
    line-height: 1.3;
    color: var(--syntry-text-strong);
    margin: 0;
  }

  &__text {
    font-size: var(--syntry-fs-body);
    line-height: 1.5;
    color: var(--syntry-text-muted);
    max-width: 34ch;
    margin: 0 0 var(--sp-3);
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    min-height: 52px;
    min-width: 200px;
    padding: 0 var(--sp-6);
    border: none;
    border-radius: var(--syntry-radius-button);
    background: var(--syntry-mark);
    color: #ffffff;
    font-family: var(--syntry-font);
    font-size: var(--syntry-fs-body);
    font-weight: 700;
    /* Mark-flavoured twin of --syntry-shadow-button, which a tenant palette repaints. */
    box-shadow: 0 12px 24px rgba(var(--syntry-mark-rgb), 0.28);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 150ms ease, background 150ms ease;

    ion-icon {
      font-size: 20px;
    }

    &:active {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 2px solid var(--syntry-mark);
      outline-offset: 3px;
    }

    /* This is the one screen mouse users actually see. */
    @media (hover: hover) {
      &:hover {
        background: var(--syntry-mark-dark);
      }
    }
  }

  &__note {
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-soft);
    margin: 0;
  }

  &__diag {
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-soft);
    margin: var(--sp-4) 0 0;

    code {
      font-family: inherit;
      font-weight: 600;
      color: var(--syntry-text-muted);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .mobile-only__btn {
    transition: none;
  }
}
</style>
